import React, { useState, useEffect } from 'react';
import { Alert, AlertDescription } from '../ui/alert';
import { Pill } from '../ui/pill';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Smartphone, Chrome, Apple, AlertTriangle, CheckCircle, ExternalLink } from 'lucide-react';
import { useTranslation } from '../../locales';
import { useLanguageStore } from '../../stores/languageStore';
import { Capacitor } from '@capacitor/core';

interface BrowserInfo {
  isSupported: boolean;
  browserName: string;
  platform: string;
  isIOS: boolean;
  isAndroid: boolean;
  isMobile: boolean;
  hasWebBluetooth: boolean;
  isSecureContext: boolean;
  isCapacitor: boolean;
}

const BrowserCompatibilityCheck: React.FC = () => {
  const { currentLanguage } = useLanguageStore();
  const t = useTranslation(currentLanguage);
  const tBc = t.browserCompatibility;
  const [browserInfo, setBrowserInfo] = useState<BrowserInfo | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const detectBrowser = (): BrowserInfo => {
      const userAgent = navigator.userAgent;
      const isIOS = /iPad|iPhone|iPod/.test(userAgent);
      const isAndroid = /Android/.test(userAgent);
      const isMobile = isIOS || isAndroid || /Mobile/.test(userAgent);

      // Capacitor 네이티브 환경 감지 (CoreBluetooth/Android BLE가 실제로 사용 가능한지)
      const isCapacitor = Capacitor.isNativePlatform();

      if (isCapacitor) {
        // Capacitor 네이티브 앱: BLE 플러그인이 CoreBluetooth/Android BLE API 직접 사용
        const platform = isIOS ? 'iOS' : isAndroid ? 'Android' : 'Native';
        return {
          isSupported: true,
          browserName: 'LINK BAND SDK',
          platform,
          isIOS,
          isAndroid,
          isMobile: true,
          hasWebBluetooth: true,   // 네이티브 BLE 플러그인으로 지원
          isSecureContext: true,   // Capacitor cleartext 설정으로 HTTP 허용
          isCapacitor: true,
        };
      }

      const hasWebBluetooth = 'bluetooth' in navigator;
      const isSecureContext = window.isSecureContext;

      let browserName = 'Unknown';
      if (userAgent.includes('Chrome')) browserName = 'Chrome';
      else if (userAgent.includes('Safari')) browserName = 'Safari';
      else if (userAgent.includes('Firefox')) browserName = 'Firefox';
      else if (userAgent.includes('Edge')) browserName = 'Edge';

      const platform = isIOS ? 'iOS' : isAndroid ? 'Android' : 'Desktop';
      const isSupported = hasWebBluetooth && isSecureContext && !isIOS;

      return {
        isSupported,
        browserName,
        platform,
        isIOS,
        isAndroid,
        isMobile,
        hasWebBluetooth,
        isSecureContext,
        isCapacitor: false,
      };
    };

    setBrowserInfo(detectBrowser());
  }, []);

  const openBluefy = () => {
    window.open('https://apps.apple.com/app/bluefy-web-ble-browser/id1492822055', '_blank');
  };

  const openWebBLE = () => {
    window.open('https://apps.apple.com/app/webble/id1193531073', '_blank');
  };

  if (!browserInfo) return null;

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-4">
      {/* Compatibility Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {browserInfo.isSupported ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-red-500" />
            )}
            {tBc.title}
          </CardTitle>
          <CardDescription>
            {tBc.currentBrowser(browserInfo.browserName, browserInfo.platform)}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <Pill size="sm" variant={browserInfo.hasWebBluetooth ? "default" : "destructive"}>
                {browserInfo.hasWebBluetooth ? tBc.supported : tBc.unsupported}
              </Pill>
              <span className="text-sm">{tBc.bluetoothAPILabel}</span>
            </div>
            <div className="flex items-center gap-2">
              <Pill size="sm" variant={browserInfo.isSecureContext ? "default" : "destructive"}>
                {browserInfo.isSecureContext ? tBc.secure : tBc.insecure}
              </Pill>
              <span className="text-sm">{tBc.securityContext}</span>
            </div>
          </div>

          {!browserInfo.isSupported && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {tBc.unsupportedAlert}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* iOS User Guide — only for web, not Capacitor native */}
      {browserInfo.isIOS && !browserInfo.isCapacitor && (
        <Card className="border-indigo-500/20 bg-indigo-500/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-indigo-300">
              <Apple className="w-5 h-5" />
              {tBc.iOSUserGuide}
            </CardTitle>
            <CardDescription className="text-indigo-400">
              {tBc.iOSSafariDesc}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="p-3 bg-card rounded-lg border border-indigo-500/20">
                <h4 className="font-semibold text-indigo-300 mb-2">{tBc.bluefyTitle}</h4>
                <p className="text-sm text-indigo-400 mb-3">
                  {tBc.bluefyDesc}
                </p>
                <Pill
                  onClick={openBluefy}
                  variant="primary"
                  className="w-full"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  {tBc.downloadBluefy}
                </Pill>
              </div>

              <div className="p-3 bg-card rounded-lg border border-indigo-500/20">
                <h4 className="font-semibold text-indigo-300 mb-2">{tBc.webBLETitle}</h4>
                <p className="text-sm text-indigo-400 mb-3">
                  {tBc.webBLEDesc}
                </p>
                <Pill
                  onClick={openWebBLE}
                  variant="secondary"
                  className="w-full"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  {tBc.downloadWebBLE}
                </Pill>
              </div>
            </div>

            <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-200">
              <h4 className="font-semibold text-yellow-400 mb-2">{tBc.howToUse}</h4>
              <ol className="text-sm text-yellow-400 space-y-1">
                <li>{tBc.step1}</li>
                <li>{tBc.step2}</li>
                <li>{tBc.step3}</li>
              </ol>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Capacitor Native App — BLE ready */}
      {browserInfo.isCapacitor && (
        <Card className="border-green-500/20 bg-green-500/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-400">
              <CheckCircle className="w-5 h-5" />
              {tBc.capacitorNativeTitle}
            </CardTitle>
            <CardDescription className="text-green-300">
              {tBc.capacitorNativeDesc}
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {/* Android User Guide */}
      {browserInfo.isAndroid && (
        <Card className="border-green-200 bg-green-500/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-400">
              <Smartphone className="w-5 h-5" />
              {tBc.androidUserGuide}
            </CardTitle>
            <CardDescription className="text-green-700">
              {browserInfo.isSupported ? 
                tBc.androidSupportedMsg :
                tBc.androidChromeMsg
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {browserInfo.isSupported ? (
              <div className="p-3 bg-card rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-400 mb-2">{tBc.androidReadyTitle}</h4>
                <p className="text-sm text-green-700">
                  {tBc.androidReadyDesc}
                </p>
              </div>
            ) : (
              <div className="p-3 bg-card rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-400 mb-2">{tBc.androidBrowserTitle}</h4>
                <p className="text-sm text-green-700 mb-3">
                  {tBc.androidBrowserDesc}
                </p>
                <div className="space-y-2">
                  <Pill
                    onClick={() => window.open('https://play.google.com/store/apps/details?id=com.android.chrome', '_blank')}
                    variant="secondary"
                    className="w-full"
                  >
                    <Chrome className="w-4 h-4 mr-2" />
                    {tBc.downloadChrome}
                  </Pill>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Desktop User Guide */}
      {!browserInfo.isMobile && (
        <Card className="border-purple-200 bg-purple-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-purple-800">
              <Chrome className="w-5 h-5" />
              {tBc.desktopUserGuide}
            </CardTitle>
            <CardDescription className="text-purple-700">
              {browserInfo.isSupported ? 
                tBc.desktopSupportedMsg :
                tBc.desktopBrowserMsg
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {browserInfo.isSupported ? (
              <div className="p-3 bg-card rounded-lg border border-purple-200">
                <h4 className="font-semibold text-purple-800 mb-2">{tBc.desktopReadyTitle}</h4>
                <p className="text-sm text-purple-700">
                  {tBc.desktopReadyDesc}
                </p>
              </div>
            ) : (
              <div className="p-3 bg-card rounded-lg border border-purple-200">
                <h4 className="font-semibold text-purple-800 mb-2">{tBc.desktopBrowserTitle}</h4>
                <p className="text-sm text-purple-700 mb-3">
                  {tBc.supportedBrowsers}
                </p>
                <ul className="text-sm text-purple-700 space-y-1">
                  <li>• Chrome 56+</li>
                  <li>• Edge 79+</li>
                  <li>• Opera 43+</li>
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Details Toggle */}
      <div className="text-center">
        <Pill
          variant="secondary"
          size="sm"
          onClick={() => setShowDetails(!showDetails)}
        >
          {showDetails ? tBc.hideDetails : tBc.showDetails}
        </Pill>
      </div>

      {/* Technical Details */}
      {showDetails && (
        <Card className="border-border bg-muted">
          <CardHeader>
            <CardTitle className="text-foreground">{tBc.technicalDetails}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-semibold">User Agent:</span>
                <p className="text-muted-foreground break-all">{navigator.userAgent}</p>
              </div>
              <div>
                <span className="font-semibold">Platform:</span>
                <p className="text-muted-foreground">{navigator.platform}</p>
              </div>
              <div>
                <span className="font-semibold">Language:</span>
                <p className="text-muted-foreground">{navigator.language}</p>
              </div>
              <div>
                <span className="font-semibold">Online:</span>
                <p className="text-muted-foreground">{navigator.onLine ? 'Yes' : 'No'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BrowserCompatibilityCheck;
